import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate } from '@tanstack/react-router';
import { VIEWS, type View } from '@pathonline/shared';
import { Layout } from './Layout.tsx';
import { SIGN_OUT_URL, useMe } from './api.ts';
import { oldDest } from './dest.ts';
import { GanttPage } from './pages/GanttPage.tsx';
import { ApplicationsPage } from './apply/Applications.tsx';
import { GuidePage, GuidesPage } from './pages/Guides.tsx';
import { SharedFolderPage } from './folders/SharedFolder.tsx';

const rootRoute = createRootRoute({ component: Layout });

/**
 * "/" — members go to the Gantt (applicants to their applications). Old links like
 * "/?dest=?view=gantt&node=T-012" (still in email inboxes) are translated here; a view the member's
 * role does not have goes to their start page instead, as a plain "/".
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (s: Record<string, unknown>): { dest?: string; signout?: string } => ({
    ...(typeof s.dest === 'string' ? { dest: s.dest } : {}),
    ...(s.signout !== undefined ? { signout: String(s.signout) } : {}),
  }),
  component: function Index() {
    const { dest, signout } = indexRoute.useSearch();
    const me = useMe();
    // Old "/?signout=1" links: sign out, then back to the start page.
    if (signout) {
      window.location.replace(SIGN_OUT_URL);
      return null;
    }
    const member = me.data?.member ? me.data : null;
    // An old view: its new page, with the node= / app= that page takes (dest.ts). An old Gantt link opens the thread.
    // A tab view this member does not have (an applicant's ?view=gantt): the start page below, not a 403 page.
    const { page, node, app, thread } = oldDest(dest ?? '');
    const view = page?.slice(1) ?? '';
    const barred = !!member && (VIEWS as readonly string[]).includes(view) && !member.nav.includes(view as View);
    if (page && !barred) return <Navigate to={page} search={{ ...(node ? { node } : {}), ...(thread ? { thread: 1 } : {}), ...(app ? { app } : {}) }} replace />;
    if (member && !member.nav.includes('gantt')) {
      if (member.group === 'applicant') return <Navigate to="/my-applications" replace />;
      const first = member.nav.find((v) => v !== 'home');
      return <Navigate to={first ? (`/${first}` as '/users') : '/guides'} replace />;
    }
    return <Navigate to="/gantt" search={node ? { node } : {}} replace />;
  },
});

/** ?node= opens a task's panel; &thread=1 (the mail's "respond" link) opens its comment thread too. */
export const ganttSearch = (s: Record<string, unknown>): { node?: string; thread?: 1 } =>
  typeof s.node === 'string' ? { node: s.node, ...(s.thread === 1 || s.thread === '1' || s.thread === true ? { thread: 1 as const } : {}) } : {};

export const ganttRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gantt',
  validateSearch: ganttSearch,
  component: GanttPage,
});

const usersRoute = createRoute({ getParentRoute: () => rootRoute, path: '/users', component: lazyRouteComponent(() => import('./users/UsersPage.tsx'), 'UsersPage') });

const personsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/persons', component: lazyRouteComponent(() => import('./users/PeoplePage.tsx'), 'PersonsPage') });
const programmesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/programmes', component: lazyRouteComponent(() => import('./users/PeoplePage.tsx'), 'ProgrammesPage') });

const nodeSearch = (s: Record<string, unknown>): { node?: string } => (typeof s.node === 'string' ? { node: s.node } : {});
const appSearch = (s: Record<string, unknown>): { app?: string } => (typeof s.app === 'string' ? { app: s.app } : {});

const commentsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/comments', validateSearch: nodeSearch, component: lazyRouteComponent(() => import('./comments/CommentsPage.tsx'), 'CommentsPage') });
const outputsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/outputs', validateSearch: nodeSearch, component: lazyRouteComponent(() => import('./outputs/OutputsPage.tsx'), 'OutputsPage') });
const networkRoute = createRoute({ getParentRoute: () => rootRoute, path: '/network', component: lazyRouteComponent(() => import('./network/NetworkPage.tsx'), 'NetworkPage') });
const managementRoute = createRoute({ getParentRoute: () => rootRoute, path: '/management', component: lazyRouteComponent(() => import('./tree/TreePage.tsx'), 'ManagementPage') });
const topicsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/topics', component: lazyRouteComponent(() => import('./tree/TreePage.tsx'), 'TopicsPage') });
const adminRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin', component: lazyRouteComponent(() => import('./tree/TreePage.tsx'), 'AdminPage') });
const strategyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/strategy', component: lazyRouteComponent(() => import('./strategy/StrategyPage.tsx'), 'StrategyPage') });

const applyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/apply', component: lazyRouteComponent(() => import('./apply/ApplyPage.tsx'), 'ApplyPage') });
// An open topic and its full description (shared topicPagePath): public, as Open Topics (Layout: under /apply).
const topicRoute = createRoute({ getParentRoute: () => rootRoute, path: '/apply/$nodeId', component: lazyRouteComponent(() => import('./apply/TopicPage.tsx'), 'TopicRoute') });
const myAppsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-applications',
  validateSearch: appSearch,
  component: function MyApps() {
    return <ApplicationsPage mode="mine" selected={myAppsRoute.useSearch().app} />;
  },
});
const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  validateSearch: appSearch,
  component: function Inbox() {
    return <ApplicationsPage mode="inbox" selected={inboxRoute.useSearch().app} />;
  },
});
const suggestRoute = createRoute({ getParentRoute: () => rootRoute, path: '/suggest', component: lazyRouteComponent(() => import('./apply/Suggestions.tsx'), 'SuggestPage') });
const suggestionsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/suggestions', component: lazyRouteComponent(() => import('./apply/Suggestions.tsx'), 'SuggestionsPage') });
const guidesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/guides', component: GuidesPage });
// ?item= : the agenda item an action's Todoist description links to.
const meetingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/meetings',
  validateSearch: (s: Record<string, unknown>): { item?: string } => (typeof s.item === 'string' ? { item: s.item } : {}),
  component: lazyRouteComponent(() => import('./meetings/MeetingsPage.tsx'), 'MeetingsPage'),
});
const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guides/$doc',
  component: function Guide() {
    return <GuidePage doc={guideRoute.useParams().doc} />;
  },
});
// A shared folder link (FOLDER_LINK_PAGE, the agenda sheets' Files column): anyone with its key, no sign-in (Layout).
const sharedFolderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/files/$nodeId/$key',
  component: function SharedFolder() {
    const { nodeId, key } = sharedFolderRoute.useParams();
    return <SharedFolderPage nodeId={nodeId} linkKey={key} />;
  },
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    indexRoute,
    ganttRoute,
    usersRoute,
    personsRoute,
    programmesRoute,
    commentsRoute,
    outputsRoute,
    networkRoute,
    managementRoute,
    topicsRoute,
    adminRoute,
    strategyRoute,
    applyRoute,
    topicRoute,
    myAppsRoute,
    inboxRoute,
    suggestRoute,
    suggestionsRoute,
    guidesRoute,
    guideRoute,
    meetingsRoute,
    sharedFolderRoute,
  ]),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
